/**
 * reCAPTCHA Enterprise.
 *
 * O pagamento com cartão passa por `POST /recarga-facil/recarga/solicitar/cartao`,
 * que exige um `recaptcha_token` — o middleware o valida com ação `PAYMENT` e
 * pontuação mínima. É o mesmo mecanismo que o `link-venda-direta` já usa; o
 * script é carregado em `public/index.html`.
 *
 * A chave de site do reCAPTCHA é **pública por natureza** (vai na URL do script
 * e no `execute`), então viver numa `REACT_APP_*` não é vazamento. Quem guarda
 * segredo é o servidor, que tem a chave secreta.
 *
 * Sem chave configurada, `executeRecaptcha` devolve `null` e a tela de cartão
 * avisa o cliente em vez de mandar uma requisição que a API recusaria.
 */

/** Ação declarada na execução; o middleware confere que bate. */
const RECAPTCHA_ACTION = 'PAYMENT';

/** Tempo máximo esperando o `grecaptcha` aparecer no `window`. */
const READY_TIMEOUT_MS = 8000;

interface GrecaptchaEnterprise {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

function getGrecaptcha(): GrecaptchaEnterprise | null {
  const grecaptcha = (window as unknown as { grecaptcha?: { enterprise?: GrecaptchaEnterprise } })
    .grecaptcha;
  return grecaptcha?.enterprise ?? null;
}

export function getRecaptchaSiteKey(): string | null {
  return process.env.REACT_APP_RECAPTCHA_SITE_KEY || null;
}

/** `true` quando há chave configurada — a tela de cartão usa para decidir. */
export function isRecaptchaConfigured(): boolean {
  return Boolean(getRecaptchaSiteKey());
}

/**
 * Executa o reCAPTCHA e devolve o token. `null` quando não há chave, quando o
 * script não carregou a tempo ou quando a execução falha — nesses casos o
 * chamador precisa tratar, nunca seguir sem o token.
 */
export async function executeRecaptcha(): Promise<string | null> {
  const siteKey = getRecaptchaSiteKey();
  if (!siteKey) return null;

  const enterprise = getGrecaptcha();
  if (!enterprise) return null;

  try {
    return await new Promise<string | null>((resolve) => {
      const timer = window.setTimeout(() => resolve(null), READY_TIMEOUT_MS);

      enterprise.ready(async () => {
        try {
          const token = await enterprise.execute(siteKey, { action: RECAPTCHA_ACTION });
          window.clearTimeout(timer);
          resolve(token || null);
        } catch {
          window.clearTimeout(timer);
          resolve(null);
        }
      });
    });
  } catch {
    return null;
  }
}
